import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { etaShort, jobLabel, nextVersionNumber } from "@veyso/core";
import { ApiError, api, type LaunchVersionsView, type ProjectDetail } from "../api.js";
import { BILLING_CHANGED } from "../components/BillingChip.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { ProgressLine } from "../components/ProgressLine.js";
import { emailNoteFor } from "../lib/eta.js";
import { useJobActivity } from "../lib/jobActivity.js";
import { useAccount } from "../auth/Gate.js";
import { useEta } from "../lib/useEta.js";
import { launchSwitchView } from "../languages/languages.js";
import { keepsSelection } from "../editor/languageView.js";
import { LanguagesDrawer } from "./LanguagesDrawer.js";
import { useVersionLanguages } from "./useVersionLanguages.js";
import { ScriptPanel } from "./ScriptPanel.js";
import { VersionStrip } from "./VersionStrip.js";
import { LanguageScript } from "../editor/LanguageScript.js";
import { RevisionChat, StudioError, type PostError } from "./RevisionChat.js";
import { chatTurns, newestReady } from "./studio.js";

const POLL_MS = 3_000;

/** Which control asked for a version, so a refusal is shown beside it. */
type Source = "script" | "chat" | "retry";

const billingChanged = () => window.dispatchEvent(new Event(BILLING_CHANGED));

/**
 * A launch video, made better a version at a time.
 *
 * The script on the left, the versions in the middle, a chat on the right.
 * Both the script and the chat make a new version, always from the newest
 * finished one; picking an older card only changes what plays and downloads.
 */
export const LaunchStudio: React.FC<{ project: ProjectDetail; onChanged: () => void }> = ({ project, onChanged }) => {
  const [view, setView] = useState<LaunchVersionsView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<{ from: Source; error: PostError } | null>(null);
  /** Whether the selected version's languages are open in the drawer. */
  const [languagesOpen, setLanguagesOpen] = useState(false);
  /** The language whose script is being checked in the drawer. */
  const [checkingOf, setCheckingOf] = useState<{ versionId: string; code: string } | null>(null);
  /** The language the player shows; null is the English. */
  const [languageOf, setLanguageOf] = useState<{ versionId: string; code: string } | null>(null);
  const wasMaking = useRef<boolean | null>(null);

  // Reads overlap: a poll can still be out when a POST asks for a fresh view.
  // Only the newest read's answer is used, so an older one that arrives late
  // cannot bring back a `making` that has ended — restarting the poll, or
  // announcing the same version twice. Nothing is set once the page is gone.
  const alive = useRef(true);
  const newestRead = useRef(0);
  const reading = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Read through a ref so polling does not restart whenever the parent
  // re-renders with a new callback.
  const changed = useRef(onChanged);
  changed.current = onChanged;

  const load = useCallback(async () => {
    const read = ++newestRead.current;
    reading.current = true;
    try {
      const next = await api.launchVersions(project.id);
      if (!alive.current || read !== newestRead.current) return;
      setView(next);
      setLoadError(null);
      // A version finished (or failed): the project's status and thumbnail
      // moved with it, and so did the balance. The new one is what plays.
      if (wasMaking.current === true && !next.making) {
        setSelectedId(null);
        changed.current();
        billingChanged();
      }
      wasMaking.current = next.making;
    } catch (cause) {
      if (!alive.current || read !== newestRead.current) return;
      setLoadError((cause as Error).message);
    } finally {
      if (read === newestRead.current) reading.current = false;
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const making = view?.making ?? false;
  useEffect(() => {
    if (!making) return;
    // A tick while a read is still out is skipped rather than stacked.
    const timer = window.setInterval(() => {
      if (!reading.current) void load();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [making, load]);

  const versions = view?.versions ?? [];
  const latest = newestReady(versions);
  const selected =
    versions.find((version) => version.id === selectedId && version.status === "ready") ?? latest ?? null;

  // The selected version's languages: for the switch on the player, and the drawer.
  const panelShown = languagesOpen && checkingOf === null;
  const languages = useVersionLanguages(project.id, selected?.id ?? null, panelShown);
  // The ready version before it, whose languages a version with none is offered.
  const previousReady = selected
    ? [...versions]
        .filter((version) => version.status === "ready" && version.number < selected.number)
        .sort((a, b) => b.number - a.number)[0]
    : undefined;
  const previousLanguages = useVersionLanguages(project.id, previousReady?.id ?? null, true);
  // Another version has its own languages and its own player: a choice made
  // on one is nothing on another, with no render in between.
  const selectedVersionId = selected?.id ?? null;
  const ownChoice = (choice: { versionId: string; code: string } | null) =>
    choice && choice.versionId === selectedVersionId ? choice.code : null;
  const checking = ownChoice(checkingOf);
  const setChecking = (code: string | null) =>
    setCheckingOf(code !== null && selectedVersionId ? { versionId: selectedVersionId, code } : null);
  const pickedCode = ownChoice(languageOf);
  const setLanguageCode = (code: string | null) =>
    setLanguageOf(code !== null && selectedVersionId ? { versionId: selectedVersionId, code } : null);
  // A language that is gone or failed lets go of the player.
  const selectionLost = pickedCode !== null && languages.view !== null && !keepsSelection(languages.view, pickedCode);
  const languageCode = selectionLost ? null : pickedCode;
  /** The language the player is showing, whose voice-over the script column shows instead of the English. */
  const shownLanguage = languageCode
    ? (languages.view?.languages.find((one) => one.code === languageCode) ?? null)
    : null;

  // Only a language still to be checked has a script to open; the rest are played.
  const switchLanguage = (code: string | null) => {
    const language = code ? languages.view?.languages.find((one) => one.code === code) : null;
    if (language?.status === "review") {
      setLanguagesOpen(true);
      setChecking(language.code);
      return;
    }
    setLanguageCode(code);
  };

  // "+ Add a language" on the Export page lands here with ?languages=open and
  // the version it was exporting: show that version, then open its drawer —
  // never another version's, which would spend credit voicing the wrong film.
  // The flags leave the address bar without telling the router: a router
  // change reloads the page around the studio, closing the drawer it opened.
  const [search] = useSearchParams();
  const askedLanguages = search.get("languages") === "open";
  const askedVersion = search.get("version");
  const openedFromAddress = useRef(false);
  useEffect(() => {
    if (!askedLanguages || !selectedVersionId || openedFromAddress.current) return;
    const wanted = askedVersion ? versions.find((one) => one.id === askedVersion && one.status === "ready") : null;
    if (wanted && wanted.id !== selectedVersionId) {
      setSelectedId(wanted.id);
      return;
    }
    openedFromAddress.current = true;
    setLanguagesOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("languages");
    url.searchParams.delete("version");
    window.history.replaceState(window.history.state, "", url);
  }, [askedLanguages, askedVersion, selectedVersionId, versions]);

  const closeLanguages = () => {
    setLanguagesOpen(false);
    setChecking(null);
  };

  // Read before the early return below: hooks cannot be called conditionally.
  const inProgress = view?.versions.find((version) => version.status === "making") ?? null;
  const account = useAccount();
  const { active: jobs } = useJobActivity();
  // The job making it, which the jobs menu counts down too. A first film whose
  // version row is not there yet is found in the feed by its project.
  const makingJobId =
    inProgress?.jobId ?? jobs.find((job) => job.projectId === project.id && job.type === "launch")?.id ?? null;
  const inProgressEta = useEta(makingJobId ?? `version-${inProgress?.id ?? "none"}`, inProgress?.eta);

  if (!view) {
    return loadError ? (
      <ErrorNote>{loadError}</ErrorNote>
    ) : (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const newest = [...view.versions].sort((a, b) => b.number - a.number)[0] ?? null;
  // A launch job from before versions has no version until it lands; the API
  // reports it as making while it can still finish.
  const firstUnderway = !newest && view.making;
  // Said only when it is true: the newest version failed, or — with no version
  // at all — the newest launch job did.
  const lastAttemptStatus = project.launch?.lastAttempt?.status ?? null;
  const failedOutright =
    newest?.status === "failed" ||
    (!newest && !view.making && (lastAttemptStatus === "failed" || lastAttemptStatus === "cancelled"));
  const nextNumber = nextVersionNumber(view.versions);

  // A failed first film, with nothing ever made: the newest try is made again.
  const retryFrom = view.canRetryFirst ? ([...view.versions].sort((a, b) => b.number - a.number)[0] ?? null) : null;

  const disabledReason = !latest
    ? inProgress || firstUnderway
      ? "Your first version is still being made."
      : retryFrom
        ? "Try the first version again before asking for changes."
        : failedOutright
          ? "The first version could not be made, so there is nothing to change yet."
          : "There is no version to change yet."
    : inProgress
      ? `Wait for v${inProgress.number} to finish.`
      : null;

  const make = async (source: Source, body: { script: string } | { message: string } | Record<string, never>) => {
    // Always from the newest finished version, whichever card is playing — or,
    // to try a failed first film again, from that film.
    const from = source === "retry" ? retryFrom : latest;
    if (!from) return false;
    setPostError(null);
    setPosting(true);
    try {
      await api.makeLaunchVersion(project.id, { from: from.id, ...body });
      if (!alive.current) return true;
      // The price is held from the balance as soon as the version starts.
      billingChanged();
      changed.current();
      await load();
      return true;
    } catch (cause) {
      const error: PostError =
        cause instanceof ApiError && (cause.code === "insufficient_credit" || cause.code === "no_credit")
          ? { kind: "credit", message: cause.message, firstFilm: source === "retry" }
          : cause instanceof ApiError && cause.code === "already_running"
            ? { kind: "running", message: cause.message }
            : { kind: "other", message: (cause as Error).message };
      if (!alive.current) return false;
      setPostError({ from: source, error });
      if (error.kind === "running") void load();
      return false;
    } finally {
      if (alive.current) setPosting(false);
    }
  };

  const lastError = failedOutright ? (newest?.error ?? project.launch?.lastAttempt?.error ?? null) : null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2.5 text-[12px] text-ink-300">
          {selected ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-signal-ok">
              <span className="inline-block h-[7px] w-[7px] rounded-full bg-signal-ok" aria-hidden="true" />
              {`v${selected.number} ready`}
            </span>
          ) : null}
          {inProgress ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-ink-200">
              <Spinner />
              Making v{inProgress.number}…
              {etaShort(inProgressEta) ? (
                <span className="font-normal text-ink-400">{etaShort(inProgressEta)}</span>
              ) : null}
            </span>
          ) : null}
          {selected?.durationSeconds ? (
            <span className="tnum font-mono">{Math.round(selected.durationSeconds)}s · 1920×1080</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {latest ? (
            // Sharing, the languages' files and every version's download live on the Export page now.
            <Link to={`/projects/${project.id}/render`} className="btn btn-secondary">
              Export &amp; share
            </Link>
          ) : null}
          {selected?.downloadUrl ? (
            <a className="btn btn-primary" href={selected.downloadUrl}>
              Download v{selected.number}
            </a>
          ) : null}
        </div>
      </div>

      {/*
        A grid, not wrapping flex: wrapping sent the chat to its own row
        whenever the page was narrower than all three columns, which on a
        laptop was always. Side by side from 1024px, stacked below it.
      */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(240px,320px)_minmax(0,1fr)_minmax(280px,360px)]">
        {shownLanguage && selected ? (
          // The script follows the language: the voice-over the player is
          // speaking, line by line, editable and revoiced free. The English
          // script is untouched until the switch goes back to it. As tall as
          // the screen at most, held in view beside the player, with the lines
          // scrolling inside: a whole video's script runs to hundreds of lines
          // and stretched the page three screens long.
          <section
            aria-label={`${shownLanguage.nativeName} voice-over`}
            className="panel flex max-h-[calc(100dvh-2rem)] min-w-0 flex-col gap-3 p-5 lg:sticky lg:top-4"
            lang={shownLanguage.code}
          >
            <div className="flex items-center gap-2">
              <h2 className="flex-1 text-[15px] font-semibold text-ink-100">{`Voice-over · ${shownLanguage.nativeName}`}</h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${
                  shownLanguage.depth === "video" ? "bg-[#E7F6EC] text-[#1C6B34]" : "bg-bay-800 text-ink-200"
                }`}
              >
                {shownLanguage.depth === "video" ? "Whole video" : "Voice and subtitles"}
              </span>
            </div>
            <p className="text-[12.5px] leading-[1.5] text-ink-300" lang="en">
              {`The ${shownLanguage.name} voice-over, line by line. The English script stays as it is.`}
            </p>
            <div className="-mr-3 min-h-0 flex-1 overflow-y-auto pr-3">
              <LanguageScript
                key={`${selected.id}:${shownLanguage.code}`}
                projectId={project.id}
                version={selected.id}
                language={shownLanguage}
                onChanged={languages.reload}
              />
            </div>
          </section>
        ) : (
          <ScriptPanel
            script={view.script}
            budget={view.scriptBudget}
            pace={view.scriptPace}
            quoteUsd={view.quoteUsd}
            nextNumber={nextNumber}
            busy={posting}
            making={Boolean(inProgress)}
            disabledReason={disabledReason}
            error={postError?.from === "script" ? postError.error : null}
            onMake={(script) => make("script", { script })}
          />
        )}

        <section className="flex min-w-0 flex-col gap-4">
          {loadError ? (
            <p role="status" className="text-[12px] text-signal-warn">
              Lost touch with the server — retrying…
            </p>
          ) : null}
          {latest ? (
            <VersionStrip
              versions={view.versions}
              selectedId={selected?.id ?? null}
              onSelect={setSelectedId}
              onLanguages={() => (languagesOpen ? closeLanguages() : setLanguagesOpen(true))}
              languagesOpen={languagesOpen}
              languages={launchSwitchView(languages.view)}
              languageCode={languageCode}
              onSwitchLanguage={switchLanguage}
            />
          ) : inProgress || firstUnderway ? (
            /*
              The wait, measured. Ten minutes is a long time for something that
              appeared by itself, and a panel that only shows a bar invites a
              refresh at two minutes and a bug report at five, so it says how far
              along it is and how long is left.
            */
            <div className="panel flex aspect-video flex-col items-center justify-center gap-3 p-10 text-center">
              <p className="text-[14px] font-medium text-ink-100">Creating your launch video</p>
              <div className="w-full max-w-[420px] text-left">
                <ProgressLine
                  id={makingJobId ?? `version-${inProgress?.id ?? "first"}`}
                  label={jobLabel("launch", inProgress?.step ?? null) ?? "Making the launch video"}
                  progress={inProgress?.progress ?? null}
                  eta={inProgress?.eta ?? { kind: "typical", seconds: 600 }}
                  note={
                    emailNoteFor(jobs, makingJobId, account?.email ?? null, "your launch video is ready") ??
                    "You can leave this page; it will be waiting on the dashboard."
                  }
                />
              </div>
            </div>
          ) : !failedOutright ? (
            <div className="panel flex aspect-video flex-col items-center justify-center gap-2 p-8 text-center">
              <p className="text-[14px] font-medium text-ink-100">No launch video yet</p>
              <p className="max-w-[44ch] text-[12.5px] leading-[1.55] text-ink-300">
                Nothing has been made for this project yet.
              </p>
            </div>
          ) : (
            <div className="panel flex aspect-video flex-col items-center justify-center gap-2 p-8 text-center">
              <p className="text-[14px] font-medium text-ink-100">This one could not be made</p>
              <p className="max-w-[44ch] text-[12.5px] leading-[1.55] text-ink-300">Nothing was charged for it.</p>
              {lastError ? <p className="max-w-[60ch] text-[12px] text-ink-400">{lastError}</p> : null}
              {retryFrom ? (
                <div className="mt-3 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={posting}
                    onClick={() => void make("retry", {})}
                  >
                    {posting ? "Starting…" : "Try again"}
                  </button>
                  <p className="text-[11.5px] text-ink-400">Charged as your launch video, not as a new version.</p>
                  {postError?.from === "retry" ? <StudioError error={postError.error} /> : null}
                </div>
              ) : null}
            </div>
          )}
        </section>

        <RevisionChat
          turns={chatTurns(view.versions)}
          quoteUsd={view.quoteUsd}
          balanceUsd={view.balanceUsd}
          busy={posting}
          disabledReason={disabledReason}
          makingNumber={inProgress?.number ?? null}
          error={postError?.from === "chat" ? postError.error : null}
          onSend={(message) => make("chat", { message })}
        />
      </div>

      {languagesOpen && selected ? (
        <LanguagesDrawer
          projectId={project.id}
          versionId={selected.id}
          versionNumber={selected.number}
          view={languages.view}
          failed={languages.failed}
          onChanged={languages.reload}
          onClose={closeLanguages}
          checking={checking}
          previous={
            previousReady && previousLanguages.view
              ? { number: previousReady.number, languages: previousLanguages.view.languages }
              : null
          }
          onCheck={setChecking}
        />
      ) : null}
    </div>
  );
};
